/**
 * The flowcharts this canvas replaces, brought across.
 *
 * Paraclete's old flowchart editor was removed; its data was not. There
 * are two dozen charts in `flowcharts` with real nodes and edges on them,
 * drawn in React Flow, and a canvas that could not read them would mean
 * re-drawing by hand every chain somebody already worked out.
 *
 * In Shadowland Title, where this canvas comes from, the legacy chart was
 * two relational tables (parties and links) and this module joined them.
 * Here it is one React Flow document per row, so the conversion is a
 * mapping rather than a join — but it plays the same part: a diagram
 * opened for the first time on a deal that has an old chart offers to
 * bring it across instead of starting empty.
 *
 * IT CONVERTS, IT DOES NOT MIGRATE. The `flowcharts` row is left exactly
 * as it is. A conversion somebody dislikes is undone by clearing the
 * canvas, and the chart it came from is still there to try again.
 */
import { makeShape, makeEdge } from './model.js';

// React Flow stores a measured size; a node that was never rendered has
// none, so fall back to something a landman's box actually looks like
// rather than to zero.
const FALLBACK = Object.freeze({ w: 220, h: 150 });

// React Flow named its handles the same way this canvas names sides, so
// a connector keeps the edge of the box it was actually drawn from
// instead of being re-solved to whichever side is nearest now.
const SIDES = new Set(['top', 'right', 'bottom', 'left']);
function legacySide(handle) {
  const side = String(handle || '').toLowerCase();
  return SIDES.has(side) ? side : undefined;
}

/** Everything a legacy node said, in the order it was shown. */
export function nodeText(node) {
  const d = node?.data || {};
  const str = [d.startingQuarterCall, d.startingSection, d.startingTownship, d.startingRange]
    .map((p) => String(p ?? '').trim()).filter(Boolean).join(' ');
  return [
    String(d.label ?? '').trim(),
    d.startingMineralInterest ? `MI ${d.startingMineralInterest}` : '',
    d.startingAcres ? `${d.startingAcres} ac` : '',
    str,
    d.interestConveyed ? String(d.interestConveyed) : '',
    d.effectiveDate ? String(d.effectiveDate).slice(0, 10) : '',
    d.bookPage ? String(d.bookPage) : '',
  ].filter(Boolean).join('\n');
}

// A legacy node type, as a canvas shape. A lease and a conveyance were
// drawn as distinct kinds of box, and keeping that distinction is the
// difference between a converted chart that reads and one that is forty
// identical rectangles.
function shapeTypeFor(node) {
  const type = String(node?.type || '').toLowerCase();
  if (type === 'person' || type === 'owner') return 'rectangle';
  if (type === 'entity' || type === 'company' || type === 'trust') return 'rectangle';
  if (type === 'lease') return 'parallelogram';
  if (type === 'deed' || type === 'conveyance') return 'rectangle';
  return 'rectangle';
}

/**
 * Build a canvas document from a legacy React Flow chart.
 *
 * Every shape carries origin 'legacy' and the node's own id, so running
 * this twice recognises what it already brought across rather than
 * stacking a second copy on top of the first.
 */
export function documentFromChart(chart = {}) {
  const nodes = Array.isArray(chart?.nodes) ? chart.nodes : [];
  const edges = Array.isArray(chart?.edges) ? chart.edges : [];
  let z = 1;

  const shapes = [];
  const shapeByNode = new Map();
  for (const node of nodes) {
    if (!node?.id) continue;
    const shape = makeShape({
      shapeType: shapeTypeFor(node),
      x: Number(node?.position?.x) || 0,
      y: Number(node?.position?.y) || 0,
      w: Number(node?.measured?.width) || Number(node?.width) || FALLBACK.w,
      h: Number(node?.measured?.height) || Number(node?.height) || FALLBACK.h,
      text: nodeText(node),
      // The final owner is the point of the chart, so it keeps the
      // emphasis it had rather than becoming one box among forty.
      bold: !!node?.data?.isFinalOwner,
      z: z++,
      origin: 'legacy',
      sourceId: String(node.id),
      sourceKind: 'node',
    });
    shapes.push(shape);
    shapeByNode.set(String(node.id), shape.id);
  }

  const out = [];
  for (const edge of edges) {
    const from = shapeByNode.get(String(edge?.source ?? ''));
    const to = shapeByNode.get(String(edge?.target ?? ''));
    // An arrow whose box did not come across would be a line to nowhere.
    if (!from || !to) continue;
    out.push(makeEdge({
      from: { shapeId: from, side: legacySide(edge?.sourceHandle) },
      to: { shapeId: to, side: legacySide(edge?.targetHandle) },
      label: String(edge?.label ?? edge?.data?.label ?? '').trim(),
      stroke: edge?.style?.stroke || undefined,
      strokeWidth: Number(edge?.style?.strokeWidth) || undefined,
      z: z++,
      origin: 'legacy',
      sourceId: String(edge?.id ?? ''),
      sourceKind: 'edge',
    }));
  }

  return { shapes, edges: out, nextZ: z };
}

/**
 * The legacy chart for a deal, if there is one worth offering.
 *
 * Anchored on the deal, and on the estimator row when the diagram is one
 * tract's rather than the whole deal's — the same two dimensions the
 * diagram itself is keyed on. A chart with nothing drawn on it is not
 * offered: "bring across 0 shapes" is a question nobody should be asked.
 */
export async function fetchLegacyChart(supabase, { dealId = null, interestEstimateId = null } = {}) {
  if (!dealId) return null;
  let query = supabase
    .from('flowcharts')
    .select('id, title, nodes, edges')
    .eq('deal_id', dealId);
  query = interestEstimateId
    ? query.eq('interest_estimate_id', interestEstimateId)
    : query.is('interest_estimate_id', null);
  const { data, error } = await query.order('updated_at', { ascending: false }).limit(1);
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  const nodes = Array.isArray(row.nodes) ? row.nodes : [];
  if (nodes.length === 0) return null;
  return row;
}

/** How to describe what came across, or null when nothing did. */
export function legacySummary(doc) {
  const shapes = doc?.shapes?.length || 0;
  const edges = doc?.edges?.length || 0;
  if (!shapes && !edges) return null;
  const boxes = `${shapes} box${shapes === 1 ? '' : 'es'}`;
  const arrows = `${edges} connector${edges === 1 ? '' : 's'}`;
  return `Brought ${boxes} and ${arrows} across from this deal's old flowchart.`;
}
